import React from 'react';
import { Box, Container, Typography, Grid, Card, CardContent, Button, Chip, List, ListItem, ListItemIcon, ListItemText, Paper } from '@mui/material';
import { Link } from 'react-router-dom';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import heroImage from '../assets/hero-image.png';
import Testimonials from '../components/Testimonials';

const HomePage = () => (
  <Box>
    {/* Hero Section */}
    <Box sx={{
      background: 'linear-gradient(135deg, #e8f5e9 0%, #ffffff 100%)',
      pt: { xs: 4, md: 16 },
      pb: { xs: 4, md: 12 },
    }}>
      <Container maxWidth="lg">
        <Grid container spacing={{ xs: 4, md: 8 }} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h1" gutterBottom sx={{
              fontWeight: 800,
              color: '#1a1a1a',
              letterSpacing: '-0.02em',
              fontSize: { xs: '2.5rem', md: '4rem' },
              lineHeight: { xs: 1.2, md: 1.1 }
            }}>
              A Gym for the <br />
              <Box component="span" sx={{ color: 'primary.main' }}>Growing Mind</Box>
            </Typography>
            <Typography variant="h5" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
              Elite cognitive development for 0–6 year olds.
              Balancing "Right Brain" speed with "Whole Child" intuition.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                size="large"
                component={Link}
                to="/schedule"
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                Book a Session
              </Button>
              <Button
                variant="outlined"
                size="large"
                component={Link}
                to="/courses"
                sx={{ px: 4, py: 1.5, fontSize: '1.1rem' }}
              >
                Explore Tracks
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              sx={{
                position: 'relative',
                '&::before': {
                  content: '""',
                  position: 'absolute',
                  top: -20,
                  right: -20,
                  width: '100%',
                  height: '100%',
                  bgcolor: 'secondary.light',
                  borderRadius: 4,
                  zIndex: 0,
                  opacity: 0.3
                }
              }}
            >
              <Box
                component="img"
                src={heroImage}
                alt="Child Learning"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                  position: 'relative',
                  zIndex: 1
                }}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* Parent Goals Section (From Flyer) */}
    <Box sx={{ py: 10, bgcolor: '#ffffff' }}>
      <Container maxWidth="lg">
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h3" gutterBottom sx={{ fontWeight: 700, color: 'primary.dark' }}>
              Are you a parent seeking to?
            </Typography>
            <List>
              {[
                "Enhance your child's learning abilities",
                "Foster creativity, critical thinking and problem-solving skills",
                "Boost your child's confidence and self-esteem",
                "Develop your child's Emotional quotient (EQ) not just Intelligence quotient (IQ)"
              ].map((text, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <ListItemIcon>
                    <CheckCircleOutlineIcon color="secondary" fontSize="large" />
                  </ListItemIcon>
                  <ListItemText
                    primary={text}
                    primaryTypographyProps={{ variant: 'h6', fontWeight: 500 }}
                  />
                </ListItem>
              ))}
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={0} sx={{ p: 4, bgcolor: '#FFF8E1', borderRadius: 4, border: '2px solid #FFECB3' }}>
              <Typography variant="h5" gutterBottom sx={{ fontWeight: 700, color: 'secondary.dark' }}>
                Introducing Right Brain Education Training
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                Don't miss this opportunity to revolutionize your child's education to unlock their full potential!
              </Typography>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                component={Link}
                to="/register"
                fullWidth
                startIcon={<AutoAwesomeIcon />}
                sx={{ color: 'black', fontWeight: 'bold' }}
              >
                Get Started Right Away
              </Button>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Box>

    {/* Empower Section (Flyer Highlight) */}
    <Box sx={{ py: 10, bgcolor: 'primary.main', color: 'white' }}>
      <Container maxWidth="md" sx={{ textAlign: 'center' }}>
        <Typography variant="h3" gutterBottom sx={{ fontWeight: 800 }}>
          Empower your child's mind
        </Typography>
        <Typography variant="h5" sx={{ mb: 4, opacity: 0.9 }}>
          The life-changing benefits of Right Brain Education
        </Typography>
        <Typography variant="body1" sx={{ fontSize: '1.2rem', maxWidth: '800px', mx: 'auto', mb: 4 }}>
          Discover the revolutionary approach to learning that unleashes creativity, confidence, and intelligence.
          Be the catalyst in your child's brilliance.
        </Typography>
      </Container>
    </Box>

    {/* Features Section (Existing) */}
    <Box sx={{ py: 12, bgcolor: '#f4f6f8' }}>
      <Container maxWidth="lg">
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="overline" color="primary" sx={{ fontWeight: 'bold', letterSpacing: 2 }}>
            Our Offerings
          </Typography>
          <Typography variant="h3" sx={{ fontWeight: 700, mt: 1 }}>
            Specialized Cognitive Tracks
          </Typography>
        </Box>
        <Grid container spacing={4}>
          {[
            {
              title: 'Right Brain Intensive',
              desc: 'Photographic memory, rapid-fire flashcards, and intuition training.',
              color: '#ffffff'
            },
            {
              title: 'Little Polyglot',
              desc: 'High-speed language immersion in Mandarin, Spanish, or French.',
              color: '#ffffff'
            },
            {
              title: 'Heart & Harmony',
              desc: 'Social-emotional learning (SEL), mindfulness, and positive affirmation.',
              color: '#ffffff'
            },
            {
              title: 'Preschool Prep',
              desc: 'Traditional literacy and numeracy delivered with high-speed stimulation.',
              color: '#ffffff'
            }
          ].map((feature, i) => (
            <Grid item xs={12} md={3} key={i}>
              <Card
                elevation={2}
                sx={{
                  height: '100%',
                  bgcolor: feature.color,
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-5px)', boxShadow: 4 }
                }}
              >
                <CardContent sx={{ p: 4 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: 'primary.main' }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {feature.desc}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>

    {/* Testimonials Section */}
    <Testimonials />

    {/* Hybrid Model Section (Existing) */}
    <Box sx={{ py: 12, bgcolor: '#ffffff' }}>
      <Container maxWidth="md">
        <Typography variant="h3" align="center" gutterBottom sx={{ fontWeight: 700 }}>
          Flexible & Hybrid
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph>
          Join us online for high-energy 30-minute sessions or visit our local Mill Creek "Social Super-Sessions".
        </Typography>
        <Grid container spacing={4} sx={{ mt: 4 }}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 4, bgcolor: '#FAFAFA', borderRadius: 4, border: '1px solid #eee', textAlign: 'center' }}>
              <Typography variant="h4" color="primary" sx={{ fontWeight: 'bold' }}>Online</Typography>
              <Typography sx={{ mt: 1 }}>Global Sessions via Explorer Portal</Typography>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box sx={{ p: 4, bgcolor: '#FAFAFA', borderRadius: 4, border: '1px solid #eee', textAlign: 'center' }}>
              <Typography variant="h4" color="secondary" sx={{ fontWeight: 'bold' }}>Local</Typography>
              <Typography sx={{ mt: 1 }}>Mill Creek In-Person Sessions</Typography>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  </Box>
);

export default HomePage;